import AgentEinsteinButton from "./AgentEinsteinButton";
import HeaderActionIcon from "./HeaderActionIcon";
import HeaderButton from "./HeaderButton";
import HeaderTitle from "./HeaderTitle";
import styles from "./Page.module.css";
import PageFooter from "./PageFooter";
import SidebarLayout from "./SidebarLayout";
import TeamSwitcherButton from "./TeamSwitcherButton";
import { HEADER_HEIGHT } from "./constants";
import posthogEvents from "@app/config/posthog/posthog-events";
import ExperimentChatPopover from "@app/routes/experiment/draft/ExperimentChatPopover/ExperimentChatPopover";
import { updateAgentChatSettings } from "@app/state/actions";
import { useAgentChatSettings } from "@app/state/store";
import type { AgentChatMode } from "@app/state/types";
import Icon from "@lib/common/components/Icon/Icon";
import ExperimentAgentChat from "@lib/experiment/agent/ExperimentAgentChat/ExperimentAgentChat";
import type { BoxProps } from "@mantine/core";
import { AppShell, Box, Group, ScrollArea, Text, Tooltip } from "@mantine/core";
import { useOs } from "@mantine/hooks";
import { spotlight } from "@mantine/spotlight";
import { usePostHog } from "posthog-js/react";
import { type ReactNode, useCallback, useMemo } from "react";
import { NavLink } from "react-router";

export default function Page({
  children,
  headerLeft,
  headerRight,
  agentic,
  contentBoxProps
}: {
  children: ReactNode;
  headerLeft?: ReactNode;
  headerRight?: ReactNode;
  agentic?: "draft" | "experiment";
  contentBoxProps?: BoxProps;
}) {
  const os = useOs();
  const posthog = usePostHog();
  const agentChatSettings = useAgentChatSettings();
  const toggleAgentChat = useCallback((open: boolean) => updateAgentChatSettings({ open }), []);
  const setAgentChatMode = useCallback((chatMode: AgentChatMode) => updateAgentChatSettings({ chatMode }), []);
  const setAgentChatSidebarWidth = useCallback(
    (width: number | string) => updateAgentChatSettings({ sidebarWidth: width }),
    []
  );

  const onHomeClick = useCallback(() => {
    posthog.capture(posthogEvents.page_header.home_button_click);
  }, [posthog]);

  const onSpotlightSearchClick = useCallback(() => {
    posthog.capture(posthogEvents.page_header.spotlight_search_button_click);
    spotlight.open();
  }, [posthog]);

  const mainContent = useMemo(
    () => (
      <ScrollArea h={`calc(100vh - ${HEADER_HEIGHT}px)`}>
        <Box
          miw={600}
          h="100%"
          px="md"
          pt="md"
          style={{ "--container-size": "var(--container-size-lg)" }}
          {...contentBoxProps}
        >
          {children}
          <PageFooter />
        </Box>
      </ScrollArea>
    ),
    [children, contentBoxProps]
  );

  const experimentAgentChat = agentic ? (
    <ExperimentAgentChat
      variant={agentic}
      toggle={toggleAgentChat}
      chatMode={agentChatSettings.chatMode}
      setChatMode={setAgentChatMode}
    />
  ) : undefined;

  return (
    <AppShell mode="fixed" padding={0} header={{ height: HEADER_HEIGHT }}>
      <AppShell.Header pr="sm" className={styles.header}>
        <Group h="100%" align="center" gap={5} justify="space-between" wrap="nowrap">
          <Group h="100%" gap="sm" wrap="nowrap">
            <Group h="100%" pl="3rem" pr="xs" gap={5} wrap="nowrap" bg="var(--mantine-color-gray-outline-hover)">
              <Page.HeaderActionIcon component={NavLink} to="/" onClick={onHomeClick}>
                <Icon name="home" />
              </Page.HeaderActionIcon>
              <Tooltip label={`Search ${os === "macos" ? "Cmd+K" : "Ctrl+K"}`}>
                <Page.HeaderActionIcon onClick={onSpotlightSearchClick}>
                  <Icon name="search" />
                </Page.HeaderActionIcon>
              </Tooltip>
              <TeamSwitcherButton />
            </Group>
            <Group gap="xs" wrap="nowrap">
              <Group gap={5} wrap="nowrap">
                {headerLeft}
              </Group>
            </Group>
          </Group>
          <Group gap="xs" wrap="nowrap">
            {headerRight}
            {agentic && (
              <>
                <Box pos="relative" top={20}>
                  <AgentEinsteinButton hide={agentChatSettings.open} onClick={() => toggleAgentChat(true)} />
                </Box>
                <ExperimentChatPopover opened={agentChatSettings.open && agentChatSettings.chatMode === "floating"}>
                  {experimentAgentChat}
                </ExperimentChatPopover>
              </>
            )}
          </Group>
        </Group>
      </AppShell.Header>
      <AppShell.Main>
        <SidebarLayout
          showSidebar={Boolean(agentic && agentChatSettings.open && agentChatSettings.chatMode === "sidebar")}
          sidebar={experimentAgentChat}
          sidebarSplitPaneProps={{
            initialWidth: agentChatSettings.sidebarWidth,
            onResizeEnd: ({ width }) => setAgentChatSidebarWidth(width)
          }}
        >
          {mainContent}
        </SidebarLayout>
      </AppShell.Main>
    </AppShell>
  );
}

Page.HeaderTitle = HeaderTitle;
Page.HeaderButton = HeaderButton;
Page.HeaderActionIcon = HeaderActionIcon;

Page.HeaderDivider = function HeaderDivider() {
  return (
    <Text size="sm" opacity={0.3}>
      /
    </Text>
  );
};
